import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { UserFormData } from '@/contexts/userForm'
import { useCheckBoxModal } from '@/hooks/useCheckBoxModal'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useLoading } from '@/hooks/useLoading'
import { useQuestion } from '@/hooks/useQuestion'
import { useStep } from '@/hooks/useStep'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import {
  CheckboxInputComponent,
  INPUT_COMPONENT_TYPE,
  NumberOrDateInputComponent,
  TextareaInputComponent,
  TextInputComponent
} from '@/types/components'
import { STEP_TYPE } from '@/types/step'

import { Checkbox } from '../CheckboxGroup/Checkbox'

const getZodSchema = ({
  type,
  id,
  minLength,
  maxLength,
  min,
  max,
  required
}: {
  type: INPUT_COMPONENT_TYPE
  id: string
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  required?: boolean
}) => {
  let zSchema

  const slug = window.location.pathname.split('/')[1]

  const getValidationMessage = (base: string, options?: Record<string, any>) => {
    const keys = [`error.validation.${base}.${id}.${slug}`, `error.validation.${base}.${id}`, `error.validation.${base}`]

    for (const key of keys) {
      if (i18n.exists(key)) return t(key, options ?? {})
    }

    return 'error.validation.default'
  }

  switch (type) {
    case INPUT_COMPONENT_TYPE.TEXT:
    case INPUT_COMPONENT_TYPE.TEXTAREA:
    case INPUT_COMPONENT_TYPE.EMAIL: {
      zSchema = z.string({
        required_error: 'error.validation.required'
      })

      if (type === INPUT_COMPONENT_TYPE.EMAIL) {
        zSchema = zSchema.email({ message: getValidationMessage('email') })
      }

      if (minLength != null)
        zSchema = zSchema.min(minLength, {
          message: getValidationMessage('min_value', { min: minLength })
        })
      if (maxLength != null)
        zSchema = zSchema.max(maxLength, {
          message: getValidationMessage('max_value', { max: maxLength })
        })
      if (required) zSchema = zSchema.nonempty({ message: getValidationMessage('required') })

      break
    }

    case INPUT_COMPONENT_TYPE.NUMBER: {
      zSchema = z.number({
        required_error: 'error.validation.required'
      })

      if (min != null) {
        zSchema = zSchema.min(min, {
          message: getValidationMessage('min_value', { min })
        })
      }
      if (max != null) {
        zSchema = zSchema.max(max, {
          message: getValidationMessage('max_value', { max })
        })
      }

      break
    }

    case INPUT_COMPONENT_TYPE.DATE: {
      zSchema = z.date({
        required_error: 'error.validation.required'
      })
      break
    }

    case INPUT_COMPONENT_TYPE.CHECKBOX: {
      zSchema = z.boolean({
        required_error: 'error.validation.required'
      })
      break
    }

    case INPUT_COMPONENT_TYPE.PASSWORD: {
      zSchema = z.string({
        required_error: 'error.validation.required'
      })
      break
    }

    default: {
      console.error('No zod schema found for type', type)
      return z.any().optional()
    }
  }

  if (!required) return zSchema.optional()

  return zSchema
}

export type InputProps = (NumberOrDateInputComponent | TextInputComponent | CheckboxInputComponent | TextareaInputComponent) & {
  value?: string | number | boolean
  tabIndex: number
  isInRow?: boolean
}

export const Input = (props: InputProps) => {
  const { t } = useTranslation()
  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { open } = useCheckBoxModal()
  const { updateQuestion, question } = useQuestion()

  const { clearFormError, addFormError, getFieldError, formErrors } = useFormErrors()

  const { showLoading } = useLoading()

  const { id, textKey, required, type, isInRow } = props
  const getInitialValue = () => {
    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM: {
        return getUserDataById(id)
      }

      case STEP_TYPE.CONSULTATION: {
        return question?.question
      }

      default: {
        return props.value ?? null
      }
    }
  }

  const [value, setValue] = useState<UserFormData | null>(getInitialValue())

  const isNumber = type === INPUT_COMPONENT_TYPE.NUMBER

  let minLength: number | undefined
  let maxLength: number | undefined
  let min: number | undefined
  let max: number | undefined
  let placeholder: string | undefined
  let checked: boolean | undefined

  switch (type) {
    case INPUT_COMPONENT_TYPE.TEXT:
    case INPUT_COMPONENT_TYPE.EMAIL:
    case INPUT_COMPONENT_TYPE.TEXTAREA:
      minLength = props.minLength
      maxLength = props.maxLength
      placeholder = props.placeholder
      break

    case INPUT_COMPONENT_TYPE.NUMBER:
    case INPUT_COMPONENT_TYPE.DATE:
      min = props.min
      max = props.max
      placeholder = props.placeholder
      break

    case INPUT_COMPONENT_TYPE.CHECKBOX:
      checked = props.checked
      break
  }

  const schema = useMemo(
    () =>
      getZodSchema({
        type,
        id,
        minLength,
        maxLength,
        min,
        max,
        required
      }),
    [type, minLength, maxLength, min, max, required, i18n.language]
  )

  const handleOnBlurValidation = (validateValue: string | boolean) => {
    const validated = schema.safeParse(isNumber ? Number(validateValue ?? 0) : validateValue)

    if (!validated.success) addFormError(id, validated.error.errors[0].message)
  }

  useEffect(() => {
    if (value === null) return

    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM:
        addData(id, isNumber ? Number(value) : value)
        break

      case STEP_TYPE.CONSULTATION:
        updateQuestion(value ? String(value) : '')
        break
    }
  }, [value])

  const error = getFieldError(id)

  useEffect(() => {
    const checkboxLabelElement = document.getElementById(`${id}-checkbox-label`)

    if (checkboxLabelElement) {
      const anchor = checkboxLabelElement.getElementsByTagName('a')[0]

      if (anchor) {
        anchor.addEventListener('click', (ev) => {
          ev.preventDefault()
          window.scrollTo(0, 0)
          // Open the modal with the url
          open(anchor.href)

          showLoading()
        })
      }
    }

    const currentData = getUserDataById(id)

    // On every language change, we update the error message
    if (!formErrors || !currentData) return

    const currentError = formErrors[id]
    if (!currentError) return // No error to update translation

    const validated = schema.safeParse(isNumber ? Number(currentData ?? 0) : currentData)

    if (!validated.success) addFormError(id, validated.error.errors[0].message)
  }, [i18n.language])

  if (type === INPUT_COMPONENT_TYPE.CHECKBOX) {
    return (
      <Checkbox
        id={id}
        textKey={textKey}
        required={required}
        checked={Boolean(value)}
        tabIndex={props.tabIndex}
        onBlur={(ev) => {
          handleOnBlurValidation(ev.target.checked)
        }}
        onChange={(ev) => {
          clearFormError(id)
          setValue(ev.target.checked)
        }}
      />
    )
  }

  if (type === INPUT_COMPONENT_TYPE.TEXTAREA) {
    return (
      <div className="relative flex flex-1 flex-col gap-1">
        <textarea
          id={id}
          tabIndex={props.tabIndex}
          required={required}
          className={`flex-1 rounded border p-4 ${error ? 'border-error' : 'border-gray'} w-full resize-none focus-visible:outline-primary`}
          placeholder={placeholder ? t(placeholder) ?? '' : ''}
          minLength={minLength}
          maxLength={maxLength}
          value={typeof value === 'string' ? value : ''}
          onChange={(ev) => {
            clearFormError(id)
            setValue(ev.target.value)
          }}
          onBlur={(ev) => {
            handleOnBlurValidation(ev.target.value)
          }}
          onFocus={() => clearFormError(id)}
        />
        <div className="absolute bottom-2 flex w-full transform justify-center px-2">
          {error ? (
            <span className="text-sm text-error" data-testid="error-span">
              {t(error)}
            </span>
          ) : minLength && typeof value === 'string' && (!value || value.length < minLength) ? (
            <span className="opacity-20">{t('inputs.textarea.min_length', { minLength })}</span>
          ) : null}
        </div>
      </div>
    )
  }

  const customClasses = {
    container: isNumber && !isInRow ? '' : 'w-full',
    input: isNumber && !isInRow ? 'w-20 text-center' : 'w-full'
  }

  return (
    <div className={`flex flex-col gap-1 ${customClasses.container}`}>
      <label htmlFor={id} className="font-bold">
        {t(textKey)} {required && '*'}
      </label>

      <input
        id={id}
        tabIndex={props.tabIndex}
        type={type}
        required={required}
        className={`rounded border p-4 ${error ? 'border-error' : 'border-gray focus-visible:outline-primary'} ${customClasses.input}`}
        onFocus={() => clearFormError(id)}
        onBlur={(ev) => {
          const val = ev.target.value.trim()
          setValue(val)
          handleOnBlurValidation(val)
        }}
        placeholder={placeholder ? t(placeholder) ?? '' : ''}
        minLength={minLength}
        maxLength={maxLength}
        checked={checked}
        min={min}
        max={max}
        value={typeof value === 'string' ? value : typeof value === 'number' ? value.toString() : ''}
        onChange={(ev) => {
          clearFormError(id)
          setValue(ev.target.value)
        }}
        onKeyDown={(evt) => {
          // Only accept numbers, dot, comma and return if the input is a number
          if (isNumber && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(evt.key) && !/[\d.,]/.test(evt.key)) {
            evt.preventDefault()
          }
        }}
      />

      {error && (
        <span className="text-sm text-error" data-testid="error-span">
          {t(error)}
        </span>
      )}
    </div>
  )
}
